// Mentor Mode (ADR-0021, operator revision 2026-08-24): the opt-in
// teaching toggle. Starting a session REQUIRES naming the mentee
// (the person being mentored) and a note. The ✨ indicator shows
// while active. 3 qualifying sessions unlock the Mentor ✨ badge.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../../lib/api';

interface MentorSession {
  id: string;
  mentor_id: string;
  mentee_id: string | null;
  started_at: string;
  ended_at: string | null;
  note: string | null;
  event_count: number;
  status: 'active' | 'ended';
}

interface ActiveResponse { session: MentorSession | null }
interface UserOption { id: string; display_name: string; handle: string }

export function MentorModeCard({ bare = false }: { bare?: boolean } = {}) {
  const qc = useQueryClient();
  const [menteeId, setMenteeId] = useState('');
  const [note, setNote] = useState('');
  const [showForm, setShowForm] = useState(false);

  const active = useQuery<ActiveResponse>({
    queryKey: ['me-mentor-active'],
    queryFn: () => api<ActiveResponse>('/api/me/mentor-sessions/active'),
    refetchInterval: 30_000,
  });
  const session = active.data?.session ?? null;
  const isActive = session?.status === 'active';

  // Mentee picker: all non-ignored users (the mentor picks who they're
  // teaching). Exclude self.
  const users = useQuery<{ users: UserOption[] }>({
    queryKey: ['users'],
    queryFn: () => api<{ users: UserOption[] }>('/api/users'),
  });
  const me = useQuery<{ user: { id: string } }>({
    queryKey: ['me'],
    queryFn: () => api<{ user: { id: string } }>('/api/me'),
  });
  const myId = me.data?.user.id;
  const menteeOptions = (users.data?.users ?? []).filter((u) => u.id !== myId);

  const start = useMutation({
    mutationFn: () => api<{ session: MentorSession }>('/api/me/mentor-sessions/start', {
      method: 'POST',
      body: { mentee_id: menteeId, note: note.trim() },
    }),
    onSuccess: () => {
      setMenteeId('');
      setNote('');
      setShowForm(false);
      void qc.invalidateQueries({ queryKey: ['me-mentor-active'] });
    },
  });

  const stop = useMutation({
    mutationFn: () => api<{ session: MentorSession }>(`/api/me/mentor-sessions/${session!.id}/stop`, {
      method: 'POST',
      body: {},
    }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['me-mentor-active'] }),
  });

  const canStart = menteeId && note.trim().length >= 3;

  // v0.8.9: `bare` prop strips the outer .card + heading so this
  // component can render inside a combined card (YourTeamAndMentorCard)
  // without nesting two notched-card frames. The two-column layout
  // owns the chrome; this renders only the body.
  const Body = (
    <>
      <p className="mb-3 text-xs text-ink-dim">
        {isActive
          ? 'You\u2019re in mentor mode. Work during this session earns a +50% XP bonus (capped +5/event). Close it when you\u2019re done.'
          : 'Opt in while you teach. Name who you\u2019re mentoring and what you\u2019re working on — 3 real sessions unlock the Mentor ✨ badge.'}
      </p>

      {isActive ? (
        <div className="space-y-2">
          <div className="text-sm text-ink-muted">
            Mentoring: <span className="text-ink font-medium">{menteeName(session.mentee_id, menteeOptions)}</span>
          </div>
          {session.note && <div className="text-sm text-ink-muted">Note: {session.note}</div>}
          <button
            onClick={() => stop.mutate()}
            disabled={stop.isPending}
            className="px-3 py-1.5 bg-amber-700 text-white text-sm rounded hover:bg-amber-800 disabled:opacity-50"
          >
            {stop.isPending ? 'Closing…' : 'End session'}
          </button>
        </div>
      ) : showForm ? (
        <div className="space-y-2">
          <div>
            <label className="block text-xs text-ink-muted mb-1">Who are you mentoring?</label>
            <select value={menteeId} onChange={(e) => setMenteeId(e.target.value)} className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm">
              <option value="">Select a teammate…</option>
              {menteeOptions.map((u) => <option key={u.id} value={u.id}>{u.display_name} (@{u.handle})</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-ink-muted mb-1">What are you working on together? (required)</label>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="e.g. pairing on the auth rewrite"
              rows={2}
              className="w-full px-3 py-2 bg-bg-base border border-border rounded text-sm"
            />
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => start.mutate()}
              disabled={!canStart || start.isPending}
              className="px-3 py-1.5 bg-brand text-white text-sm rounded hover:bg-brand-dark disabled:opacity-50"
            >
              {start.isPending ? 'Starting…' : 'Start session'}
            </button>
            <button onClick={() => setShowForm(false)} className="px-3 py-1.5 text-sm text-ink-muted hover:text-ink">Cancel</button>
          </div>
          {!canStart && <p className="text-xs text-amber-400">Pick a mentee and write a note (3+ chars) to start.</p>}
        </div>
      ) : (
        <button onClick={() => setShowForm(true)} className="px-3 py-1.5 bg-brand text-white text-sm rounded hover:bg-brand-dark">
          Start mentor mode
        </button>
      )}
    </>
  );

  if (bare) return Body;

  return (
    <div className="card">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">
          {isActive ? '✨ Mentor Mode — active' : 'Mentor Mode'}
        </h2>
        {isActive && <span className="text-xs text-ink-muted">since {new Date(session.started_at).toLocaleTimeString()}</span>}
      </div>
      {Body}
    </div>
  );
}

function menteeName(id: string | null, options: UserOption[]): string {
  if (!id) return '—';
  const u = options.find((o) => o.id === id);
  return u ? u.display_name : id;
}
